// src/services/locales.js - Servicio de gestión de locales

import AuthService from "../auth/auth.js";
import UsuariosService from "../usuarios/usuarios.js";

/**
 * Servicio para manejar todas las operaciones relacionadas con locales
 * Conecta con el endpoint /locales/api/local/ del backend
 * Siguiendo el patrón establecido en dispositivos.js
 */
class LocalesService {
  constructor() {
    this.baseURL = process.env.VUE_APP_API_URL;
    this.endpoint = "/locales/api/local/";
    this.maxImageSize = 5 * 1024 * 1024; // 5MB
    this.allowedImageTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
  }

  /**
   * Obtiene todos los locales del backend
   * @returns {Promise<Array>} Array de locales mapeados
   */
  async obtenerTodosLosLocales() {
    try {
      const response = await AuthService.authenticatedRequest(this.endpoint);

      // Manejo robusto de la respuesta
      let locales = [];
      if (Array.isArray(response)) {
        locales = response;
      } else if (response && Array.isArray(response.results)) {
        locales = response.results;
      } else {
        locales = [];
      }

      // Obtener encargados para mapeo
      const encargados = await this.obtenerEncargadosParaMapeo();

      // Mapear locales con información de encargados
      const localesMapeados = locales.map((local) => this.mapearLocalBackendAFrontend(local, encargados));

      return localesMapeados;
    } catch (error) {
      throw this.manejarError(error);
    }
  }

  /**
   * Obtiene locales filtrados por empresa
   * @param {number} empresaId - ID de la empresa
   * @returns {Promise<Array>} Array de locales de la empresa
   */
  async obtenerLocalesPorEmpresa(empresaId) {
    try {
      const todosLosLocales = await this.obtenerTodosLosLocales();

      // Filtrar por empresa
      const localesEmpresa = todosLosLocales.filter((local) => local.id_empresa === empresaId || local.id_empresa === parseInt(empresaId));

      return localesEmpresa;
    } catch (error) {
      throw this.manejarError(error);
    }
  }

  /**
   * Obtiene un local específico por ID
   * @param {number} localId - ID del local
   * @returns {Promise<Object>} Local mapeado
   */
  async obtenerLocalPorId(localId) {
    try {
      const response = await AuthService.authenticatedRequest(`${this.endpoint}${localId}/`);

      // Obtener encargados para mapeo
      const encargados = await this.obtenerEncargadosParaMapeo();

      const localMapeado = this.mapearLocalBackendAFrontend(response, encargados);

      return localMapeado;
    } catch (error) {
      throw this.manejarError(error);
    }
  }

  /**
   * Crea un nuevo local junto con su plano
   * @param {Object} datosLocal - Datos del local a crear
   * @param {File|null} archivoImagen - Archivo de imagen (logo) opcional
   * @param {File|null} archivoPlano - Archivo de imagen del plano (Obligatorio para crear)
   * @returns {Promise<Object>} Local creado y mapeado
   */
  async crearLocal(datosLocal, archivoImagen = null, archivoPlano = null) {
    try {
      this.validarDatosLocal(datosLocal);
      if (archivoImagen) this.validarArchivoImagen(archivoImagen);

      const formData = this.construirFormData(datosLocal, archivoImagen);
      const tokens = this.obtenerTokens();

      let endpointDestino = `${this.baseURL}${this.endpoint}`;

      if (archivoPlano) {
        endpointDestino = `${this.baseURL}/locales/api/local/crear-con-plano/`;
        formData.append("plano_archivo", archivoPlano);
        formData.append("plano_filas", datosLocal.plano_filas || 10);
        formData.append("plano_columnas", datosLocal.plano_columnas || 10);
        formData.append("plano_activo", "true");
      }

      const response = await fetch(endpointDestino, {
        method: "POST",
        credentials: "include",
        headers: {
          "X-CSRFToken": tokens.csrf || "",
          ...(tokens.jwt ? { Authorization: `Bearer ${tokens.jwt}` } : {}),
        },
        body: formData,
      });

      const responseData = await this.procesarRespuesta(response);
      const dataLocalReal = responseData.local ? responseData.local : responseData;
      const encargados = await this.obtenerEncargadosParaMapeo();
      return this.mapearLocalBackendAFrontend(dataLocalReal, encargados);
    } catch (error) {
      throw this.manejarError(error);
    }
  }

  /**
   * Actualiza un local existente
   * @param {number} localId - ID del local a actualizar
   * @param {Object} datosLocal - Datos actualizados del local
   * @param {File|null} archivoImagen - Nuevo archivo de imagen
   * @param {boolean} eliminarImagenActual - Si se debe eliminar la imagen actual
   * @returns {Promise<Object>} Local actualizado y mapeado
   */
  async actualizarLocal(
    localId,
    datosLocal,
    archivoImagen = null,
    eliminarImagenActual = false,
    archivoPlano = null,
    eliminarPlanoActual = false,
  ) {
    try {
      this.validarDatosLocal(datosLocal);
      if (archivoImagen) this.validarArchivoImagen(archivoImagen);

      if (eliminarPlanoActual) {
        await this.eliminarPlanoLocal(localId);
      }
      const formData = this.construirFormData(datosLocal, archivoImagen, eliminarImagenActual);
      const tokens = this.obtenerTokens();

      const response = await fetch(`${this.baseURL}${this.endpoint}${localId}/`, {
        method: "PATCH",
        credentials: "include",
        headers: {
          "X-CSRFToken": tokens.csrf || "",
          ...(tokens.jwt ? { Authorization: `Bearer ${tokens.jwt}` } : {}),
        },
        body: formData,
      });

      const responseData = await this.procesarRespuesta(response);

      if (archivoPlano) {
        const formDataPlano = new FormData();
        formDataPlano.append("archivo", archivoPlano);
        formDataPlano.append("filas", datosLocal.plano_filas || 10);
        formDataPlano.append("columnas", datosLocal.plano_columnas || 10);
        formDataPlano.append("activo", "true");

        const responsePlano = await fetch(`${this.baseURL}${this.endpoint}${localId}/plano/`, {
          method: "POST",
          credentials: "include",
          headers: {
            "X-CSRFToken": tokens.csrf || "",
            ...(tokens.jwt ? { Authorization: `Bearer ${tokens.jwt}` } : {}),
          },
          body: formDataPlano,
        });

        if (!responsePlano.ok) {
          console.warn("El local se actualizó, pero hubo un error al guardar el nuevo plano.");
        }
      }

      const encargados = await this.obtenerEncargadosParaMapeo();
      return this.mapearLocalBackendAFrontend(responseData, encargados);
    } catch (error) {
      throw this.manejarError(error);
    }
  }

  /**
   * Obtiene el plano de un local
   * @param {number} localId - ID del local
   * @returns {Promise<Object|null>} Datos del plano o null si no tiene
   */
  async obtenerPlanoLocal(localId) {
    try {
      const tokens = this.obtenerTokens();
      const response = await fetch(`${this.baseURL}${this.endpoint}${localId}/plano/`, {
        method: "GET",
        headers: {
          ...(tokens.jwt ? { Authorization: `Bearer ${tokens.jwt}` } : {}),
        },
      });
      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error(`Error al obtener plano: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  /**
   * Elimina el plano actual de un local
   * @param {number} localId - ID del local
   */
  async eliminarPlanoLocal(localId) {
    const tokens = this.obtenerTokens();
    const response = await fetch(`${this.baseURL}${this.endpoint}${localId}/plano/`, {
      method: "DELETE",
      credentials: "include",
      headers: {
        "X-CSRFToken": tokens.csrf || "",
        ...(tokens.jwt ? { Authorization: `Bearer ${tokens.jwt}` } : {}),
      },
    });

    if (!response.ok && response.status !== 404) {
      throw new Error("No se pudo eliminar el plano");
    }
    return true;
  }

  // ==========================================
  // MÉTODOS HELPER PRIVADOS
  // ==========================================

  /**
   * Obtiene encargados para mapeo con manejo de errores
   * @returns {Promise<Array>} Array de encargados o array vacío
   */
  async obtenerEncargadosParaMapeo() {
    try {
      return await UsuariosService.obtenerEncargados();
    } catch (error) {
      return [];
    }
  }

  /**
   * Mapea un local del formato backend al formato frontend
   * @param {Object} localBackend - Local en formato backend
   * @param {Array} encargados - Array de encargados disponibles
   * @returns {Object} Local en formato frontend
   */
  mapearLocalBackendAFrontend(localBackend, encargados = []) {
    try {
      // Buscar información del encargado
      const encargado = localBackend.id_encargado ? encargados.find((enc) => enc.id === localBackend.id_encargado) : null;

      const localMapeado = {
        // Campos directos del backend
        id: localBackend.id,
        numero_local: localBackend.numero_local,
        direccion: localBackend.direccion,
        id_empresa: localBackend.id_empresa,
        id_encargado: localBackend.id_encargado || null,
        media: localBackend.media || null,
        fecha_creacion: localBackend.fecha_creacion,
        fecha_actualizacion: localBackend.fecha_actualizacion,

        // Información del encargado mapeada
        encargado: encargado ? encargado.nombre_completo : "Sin asignar",
        encargado_email: encargado ? encargado.email : null,
        encargado_telefono: encargado ? encargado.telefono || "No disponible" : null,
        encargado_cargo: encargado ? encargado.tipo_nombre : null,
        encargado_activo: encargado ? encargado.is_active : false,

        // Indicadores de estado
        tiene_encargado: !!localBackend.id_encargado,
        disponible_para_asignar: !localBackend.id_encargado,
        estado: localBackend.estado || "Cargando...",

        p_op100: localBackend.p_op100 ?? 0,
        p_op80: localBackend.p_op80 ?? 0,
        p_op60: localBackend.p_op60 ?? 0,
        p_op50: localBackend.p_op50 ?? 0,
        p_op40: localBackend.p_op40 ?? 0,
        p_op20: localBackend.p_op20 ?? 0,
        p_op0: localBackend.p_op0 ?? 0,

        aqi: localBackend.aqi ?? 0,
        temperatura_ciudad: localBackend.temperatura_ciudad ?? 0,
        temperatura_interior: localBackend.temperatura_interior ?? null,
        max_temperatura_interior: localBackend.max_temperatura_interior ?? null,
        min_temperatura_interior: localBackend.min_temperatura_interior ?? null,

        // Campos críticos para métricas
        total_dispositivos: localBackend.total_dispositivos ?? 0,
        total_dispositivos_operativos: localBackend.total_dispositivos_operativos ?? 0,
        total_dispositivos_fuera_de_servicio: localBackend.total_dispositivos_fuera_de_servicio ?? 0,
        total_alertas_activas: localBackend.total_alertas_activas ?? 0,

        ultima_actualizacion: localBackend.fecha_actualizacion || localBackend.fecha_creacion,

        nombre: `Local ${localBackend.numero_local}`,
        _imageError: false,
      };

      return localMapeado;
    } catch (error) {
      return {
        id: localBackend.id,
        numero_local: localBackend.numero_local || 0,
        direccion: localBackend.direccion || "Sin dirección",
        id_empresa: localBackend.id_empresa || null,
        id_encargado: localBackend.id_encargado || null,
        media: localBackend.media || null,

        total_dispositivos: localBackend.total_dispositivos ?? 0,
        total_dispositivos_operativos: localBackend.total_dispositivos_operativos ?? 0,
        total_dispositivos_fuera_de_servicio: localBackend.total_dispositivos_fuera_de_servicio ?? 0,
        total_alertas_activas: localBackend.total_alertas_activas ?? 0,

        temperatura_interior: null,
        max_temperatura_interior: null,
        min_temperatura_interior: null,

        // NUEVO: Valores por defecto en catch
        p_op100: localBackend.p_op100 ?? 0,
        p_op80: localBackend.p_op80 ?? 0,
        p_op60: localBackend.p_op60 ?? 0,
        p_op50: localBackend.p_op50 ?? 0,
        p_op40: localBackend.p_op40 ?? 0,
        p_op20: localBackend.p_op20 ?? 0,
        p_op0: localBackend.p_op0 ?? 0,
        aqi: localBackend.aqi ?? 0,

        encargado: "Sin asignar",
        tiene_encargado: false,
        nombre: `Local ${localBackend.numero_local || localBackend.id}`,
        _mapError: true,
      };
    }
  }

  /**
   * Valida los datos básicos del local
   * @param {Object} datosLocal - Datos a validar
   */
  validarDatosLocal(datosLocal) {
    if (!datosLocal) {
      throw new Error("Los datos del local son requeridos");
    }

    if (!datosLocal.numero_local || !datosLocal.numero_local.toString().trim()) {
      throw new Error("El número de local es requerido");
    }

    if (!datosLocal.direccion || !datosLocal.direccion.trim()) {
      throw new Error("La dirección es requerida");
    }

    if (!datosLocal.id_empresa) {
      throw new Error("El ID de empresa es requerido");
    }

    // Validar longitudes
    if (datosLocal.numero_local.toString().length > 50) {
      throw new Error("El número de local no puede tener más de 50 caracteres");
    }

    if (datosLocal.direccion.length > 200) {
      throw new Error("La dirección no puede tener más de 200 caracteres");
    }
  }

  /**
   * Valida archivo de imagen
   * @param {File} archivo - Archivo a validar
   */
  validarArchivoImagen(archivo) {
    if (!archivo.type.startsWith("image/")) {
      throw new Error("El archivo debe ser una imagen válida (JPG, PNG, GIF)");
    }

    if (!this.allowedImageTypes.includes(archivo.type)) {
      throw new Error("Formato de imagen no soportado. Use JPG, PNG o GIF");
    }

    if (archivo.size > this.maxImageSize) {
      throw new Error("El archivo es muy grande. El tamaño máximo es 5MB");
    }
  }

  /**
   * Construye FormData para envío
   * @param {Object} datosLocal - Datos del local
   * @param {File|null} archivoImagen - Archivo de imagen opcional
   * @param {boolean} eliminarImagenActual - Si eliminar imagen actual
   * @returns {FormData} FormData preparado
   */
  construirFormData(datosLocal, archivoImagen = null, eliminarImagenActual = false) {
    const formData = new FormData();

    // Campos básicos
    formData.append("numero_local", datosLocal.numero_local.toString().trim());
    formData.append("direccion", datosLocal.direccion.trim());
    formData.append("id_empresa", datosLocal.id_empresa);

    // Manejo del encargado
    if (datosLocal.id_encargado && datosLocal.id_encargado !== "") {
      formData.append("id_encargado", parseInt(datosLocal.id_encargado));
    } else {
      formData.append("id_encargado", "");
    }

    // Manejo de imagen
    if (archivoImagen) {
      formData.append("media", archivoImagen);
    } else if (eliminarImagenActual) {
      formData.append("media", "");
    }

    return formData;
  }

  /**
   * Obtiene tokens de autenticación desde cookies
   * @returns {Object} Objeto con tokens csrf y jwt
   */
  obtenerTokens() {
    const getCookie = (name) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(";").shift();
      return null;
    };

    return {
      csrf: getCookie("csrftoken"),
      jwt: getCookie("iiotefintel_cookie"),
    };
  }

  /**
   * Procesa la respuesta HTTP y maneja errores
   * @param {Response} response - Respuesta fetch
   * @returns {Promise<Object>} Datos de respuesta parseados
   */
  async procesarRespuesta(response) {
    const responseText = await response.text();

    if (!response.ok) {
      let errorData;
      try {
        errorData = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error(`Error del servidor (${response.status}): ${responseText}`);
      }

      // Construir mensaje de error detallado
      let mensajeError = "Error al procesar local:\n";

      if (errorData.numero_local) {
        const numeroErrors = Array.isArray(errorData.numero_local) ? errorData.numero_local : [errorData.numero_local];
        mensajeError += `• Número de local: ${numeroErrors.join(", ")}\n`;
      }

      if (errorData.direccion) {
        const direccionErrors = Array.isArray(errorData.direccion) ? errorData.direccion : [errorData.direccion];
        mensajeError += `• Dirección: ${direccionErrors.join(", ")}\n`;
      }

      if (errorData.id_encargado) {
        const encargadoErrors = Array.isArray(errorData.id_encargado) ? errorData.id_encargado : [errorData.id_encargado];
        mensajeError += `• Encargado: ${encargadoErrors.join(", ")}\n`;
      }

      if (errorData.media) {
        const mediaErrors = Array.isArray(errorData.media) ? errorData.media : [errorData.media];
        mensajeError += `• Imagen: ${mediaErrors.join(", ")}\n`;
      }

      if (errorData.non_field_errors) {
        const generalErrors = Array.isArray(errorData.non_field_errors) ? errorData.non_field_errors : [errorData.non_field_errors];
        mensajeError += `• ${generalErrors.join(", ")}\n`;
      }

      throw new Error(mensajeError.trim() || `Error del servidor (${response.status})`);
    }

    // Parsear respuesta exitosa
    try {
      return JSON.parse(responseText);
    } catch (parseError) {
      throw new Error("Respuesta del servidor no válida");
    }
  }

  /**
   * Maneja errores y los convierte en mensajes amigables
   * @param {Error} error - Error original
   * @returns {Error} Error procesado
   */
  manejarError(error) {
    let mensaje = "Error desconocido";
    let codigo = "UNKNOWN_ERROR";

    if (error.message?.includes("Sesión expirada")) {
      mensaje = "Sesión expirada. Inicie sesión nuevamente.";
      codigo = "SESSION_EXPIRED";
    } else if (error.message?.includes("No hay sesión activa")) {
      mensaje = "No hay sesión activa. Inicie sesión.";
      codigo = "NO_SESSION";
    } else if (error.message?.includes("Error 401")) {
      mensaje = "No autorizado. Inicie sesión nuevamente.";
      codigo = "UNAUTHORIZED";
    } else if (error.message?.includes("Error 403")) {
      mensaje = "Sin permisos para gestionar locales";
      codigo = "FORBIDDEN";
    } else if (error.message?.includes("Error 404")) {
      mensaje = "Local no encontrado";
      codigo = "NOT_FOUND";
    } else if (error.message?.includes("Error 500")) {
      mensaje = "Error interno del servidor";
      codigo = "SERVER_ERROR";
    } else if (error.message?.includes("Failed to fetch")) {
      mensaje = "Error de conexión. Verifique su internet.";
      codigo = "NETWORK_ERROR";
    } else if (error.message) {
      // Preservar mensajes de validación específicos
      mensaje = error.message;
    }

    const errorProcesado = new Error(mensaje);
    errorProcesado.codigo = codigo;
    errorProcesado.original = error;

    return errorProcesado;
  }

  // ==========================================
  // MÉTODOS UTILITARIOS
  // ==========================================

  /**
   * Obtiene URL completa de imagen
   * @param {string} mediaPath - Ruta de la imagen
   * @returns {string|null} URL completa o null
   */
  obtenerUrlImagen(mediaPath) {
    if (!mediaPath) return null;

    if (mediaPath.startsWith("http")) {
      return mediaPath;
    }

    const cleanPath = mediaPath.startsWith("/") ? mediaPath : `/${mediaPath}`;
    return `${this.baseURL}${cleanPath}`;
  }

  /**
   * Obtiene estadísticas de locales
   * @param {Array} locales - Array de locales
   * @returns {Object} Objeto con estadísticas
   */
  obtenerEstadisticasLocales(locales) {
    return {
      total: locales.length,
      conEncargado: locales.filter((l) => l.tiene_encargado).length,
      sinEncargado: locales.filter((l) => !l.tiene_encargado).length,
      // Actualizado a los nuevos estados:
      operativos: locales.filter((l) => this.calcularEstadoLocal(l) === "Operativo").length,
      fueraDeServicio: locales.filter((l) => this.calcularEstadoLocal(l) === "Fuera de servicio").length,
      sinDispositivos: locales.filter((l) => this.calcularEstadoLocal(l) === "Sin dispositivos").length,
    };
  }

  /**
   * Calcula el estado operativo de un local basado en los nuevos campos p_op
   * @param {Object} local - Objeto local mapeado
   * @returns {string} Estado del local
   */
  calcularEstadoLocal(local) {
    if (local.estado && local.estado !== "Cargando...") {
      // Prevención por si el backend llega a mandar un estado antiguo
      if (local.estado === "Error") return "Fuera de servicio";
      if (local.estado === "Advertencia") return "Operativo";
      return local.estado;
    }

    if (local.total_dispositivos === 0) {
      return "Sin dispositivos";
    }

    const totalPorcentaje = local.p_op100 + local.p_op80 + local.p_op60 + local.p_op50 + local.p_op40 + local.p_op20 + local.p_op0;

    if (totalPorcentaje === 0) {
      return "Sin dispositivos";
    }

    if (local.p_op0 === 100) {
      return "Fuera de servicio";
    }

    return "Operativo";
  }

  // ==========================================
  // MÉTODOS DE ELIMINACIÓN CON VALIDACIONES
  // ==========================================

  /**
   * Verifica si un local tiene dispositivos asociados
   * @param {number} localId - ID del local a verificar
   * @returns {Promise<Object>} Objeto con información de dispositivos
   */
  async verificarDispositivosEnLocal(localId) {
    try {
      // Obtener dispositivos del local usando DispositivosService
      // Importamos dinámicamente para evitar dependencias circulares
      const DispositivosService = (await import("../dispositivos/dispositivos.js")).default;
      const dispositivos = await DispositivosService.obtenerDispositivosPorLocal(localId);

      const result = {
        tieneDispositivos: dispositivos.length > 0,
        cantidadDispositivos: dispositivos.length,
        dispositivos: dispositivos,
      };

      return result;
    } catch (error) {
      // En caso de error, asumimos que tiene dispositivos por seguridad
      return {
        tieneDispositivos: true,
        cantidadDispositivos: 0,
        dispositivos: [],
        error: error.message,
      };
    }
  }

  /**
   * Elimina un local después de verificar que no tenga dispositivos
   * @param {number} localId - ID del local a eliminar
   * @param {boolean} forzarEliminacion - Si true, elimina aunque tenga dispositivos (usar con precaución)
   * @returns {Promise<boolean>} Éxito de la operación
   */
  async eliminarLocal(localId, forzarEliminacion = false) {
    try {
      // 1. Verificar si el local tiene dispositivos (a menos que se fuerce)
      if (!forzarEliminacion) {
        const verificacion = await this.verificarDispositivosEnLocal(localId);

        if (verificacion.tieneDispositivos) {
          const error = new Error(
            `No se puede eliminar el local porque tiene ${verificacion.cantidadDispositivos} dispositivo(s) asociado(s). ` +
              "Primero debes reasignar o eliminar todos los dispositivos de este local.",
          );
          error.codigo = "LOCAL_HAS_DEVICES";
          error.cantidadDispositivos = verificacion.cantidadDispositivos;
          error.dispositivos = verificacion.dispositivos;
          throw error;
        }
      }

      // 2. Realizar petición de eliminación al backend
      const response = await fetch(`${this.baseURL}${this.endpoint}${localId}/`, {
        method: "DELETE",
        credentials: "include",
        headers: {
          "X-CSRFToken": this.obtenerTokens().csrf || "",
          ...(this.obtenerTokens().jwt ? { Authorization: `Bearer ${this.obtenerTokens().jwt}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      return true;
    } catch (error) {
      throw this.manejarError(error);
    }
  }

  /**
   * Obtiene información detallada para eliminación de local
   * @param {number} localId - ID del local
   * @returns {Promise<Object>} Información del local y sus dependencias
   */
  async obtenerInfoEliminacion(localId) {
    try {
      // Obtener datos del local
      const local = await this.obtenerLocalPorId(localId);

      // Verificar dispositivos
      const verificacionDispositivos = await this.verificarDispositivosEnLocal(localId);

      const info = {
        local: local,
        puedeEliminar: !verificacionDispositivos.tieneDispositivos,
        razonNoEliminar: verificacionDispositivos.tieneDispositivos
          ? `El local tiene ${verificacionDispositivos.cantidadDispositivos} dispositivo(s) asociado(s)`
          : null,
        dispositivos: verificacionDispositivos.dispositivos,
        cantidadDispositivos: verificacionDispositivos.cantidadDispositivos,
        advertencias: [],
      };

      // Agregar advertencias adicionales
      if (local.tiene_encargado) {
        info.advertencias.push(`El local tiene un encargado asignado: ${local.encargado}`);
      }

      if (local.media) {
        info.advertencias.push("El local tiene un logo/imagen que será eliminado");
      }

      return info;
    } catch (error) {
      throw this.manejarError(error);
    }
  }
}

// Exportar instancia única del servicio
export default new LocalesService();
